import React, { useEffect, useState } from 'react';
import {
  Typography,
  Divider,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import { API } from 'utilities/api';
import { slugify } from './utils';

/**
 * Page-level settings dialog — SEO, language, homepage / public flags,
 * and custom CSS / JS. Edits stay local until the parent saves.
 */
export default function PageSettingsDrawer({ open, onClose, page, onSave, isSubmitting, brandId }) {
  const [form, setForm] = useState({});
  const [subdomains, setSubdomains] = useState([]);
  // The brand's verified domain, so the options read as real addresses rather
  // than "shop.yourdomain", which tells nobody what their page's URL will be.
  const [domain, setDomain] = useState('');

  // The brand's verified subdomains, so choosing where a page lives is a
  // pick from what exists rather than typing a label that routes nowhere.
  useEffect(() => {
    if (!open || !brandId) return;
    API.listBrandSubdomains?.({ brand_id: brandId })
      .then((res) => setSubdomains(Array.isArray(res) ? res : []))
      .catch(() => setSubdomains([]));
    API.listBrandDomains?.({ brand_id: brandId })
      .then((res) => {
        const primary = (Array.isArray(res) ? res : [])
          .find((d) => d.status === 'verified') || (Array.isArray(res) ? res[0] : null);
        setDomain(primary?.domain || '');
      })
      .catch(() => setDomain(''));
  }, [open, brandId]);

  useEffect(() => {
    if (open && page) {
      setForm({
        title: page.title || '',
        slug: page.slug || '',
        seoTitle: page.seoTitle || '',
        seoDescription: page.seoDescription || '',
        seoKeywords: page.seoKeywords || '',
        language: page.language || 'en',
        isHomepage: !!page.isHomepage,
        isPublic: page.isPublic !== false,
        customCss: page.customCss || '',
        customJs: page.customJs || '',
        detailOf: page.detailOf || '',
        subdomain: page.subdomain || '',
      });
    }
  }, [open, page]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        Page settings
        <GlassIconButton label='Close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Title and address live here now. The title was editable in the
            toolbar and the address was not editable at all, so renaming a
            page meant deleting it and making a new one — losing its content.
            Changing the address does break existing links to it, which is
            why that is said out loud rather than discovered later. */}
        <TextField
          size='small'
          label='Page title'
          value={form.title || ''}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          fullWidth
          sx={{ mb: '14px' }}
        />
        <TextField
          size='small'
          label='Address (URL path)'
          value={form.slug || ''}
          onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
          fullWidth
          helperText={form.slug !== page?.slug
            ? `Moving from /${page?.slug} to /${form.slug || ''} — old links to this page will stop working.`
            : `This page lives at /${form.slug || ''}`}
          sx={{ mb: '18px' }}
        />
        <Divider sx={{ mb: '18px' }} />
        <Stack spacing='14px'>
          <TextField
            size='small'
            label='SEO title'
            value={form.seoTitle || ''}
            onChange={(e) => setForm({ ...form, seoTitle: e.target.value })}
            placeholder='Shown in search results'
            fullWidth
          />
          <TextField
            size='small'
            label='SEO description'
            multiline
            minRows={3}
            value={form.seoDescription || ''}
            onChange={(e) => setForm({ ...form, seoDescription: e.target.value })}
            fullWidth
          />
          <TextField
            size='small'
            label='SEO keywords'
            value={form.seoKeywords || ''}
            onChange={(e) => setForm({ ...form, seoKeywords: e.target.value })}
            placeholder='comma, separated'
            fullWidth
          />
          {/* Page width lives here as well as in the create dialog. It was
              only ever asked about once, at creation, and then had nowhere to
              be changed — so a page created as normal width could never
              become full width, and the only way round it was to build the
              page again. Anything asked at creation belongs here too. */}
          <FormControl size='small' fullWidth>
            <InputLabel>Page width</InputLabel>
            <Select
              label='Page width'
              value={form.layout || 'default'}
              onChange={(e) => setForm({ ...form, layout: e.target.value })}
            >
              <MenuItem value='default'>Normal — centred, up to 1200px</MenuItem>
              <MenuItem value='sidebar'>Narrower — up to 1100px</MenuItem>
              <MenuItem value='fullwidth'>Full width — edge to edge</MenuItem>
            </Select>
          </FormControl>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px' alignItems='center'>
            <FormControl size='small' sx={{ maxWidth: 160, minWidth: 130 }}>
              <InputLabel>Language</InputLabel>
              <Select
                label='Language'
                value={form.language || 'en'}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
              >
                <MenuItem value='en'>en</MenuItem>
                <MenuItem value='fr'>fr</MenuItem>
                <MenuItem value='es'>es</MenuItem>
                <MenuItem value='de'>de</MenuItem>
                <MenuItem value='fa'>fa</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!form.isHomepage}
                  onChange={(e) => setForm({ ...form, isHomepage: e.target.checked })}
                />
              }
              label='Set as homepage'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isPublic !== false}
                  onChange={(e) => setForm({ ...form, isPublic: e.target.checked })}
                />
              }
              label='Public'
            />
          </Stack>
          <TextField
            size='small'
            label='Custom CSS'
            multiline
            minRows={4}
            value={form.customCss || ''}
            onChange={(e) => setForm({ ...form, customCss: e.target.value })}
            fullWidth
            sx={{ '& textarea': { fontFamily: 'monospace' } }}
          />
          <TextField
            size='small'
            label='Custom JS'
            multiline
            minRows={4}
            value={form.customJs || ''}
            onChange={(e) => setForm({ ...form, customJs: e.target.value })}
            fullWidth
            sx={{ '& textarea': { fontFamily: 'monospace' } }}
          />
        </Stack>
        <Divider sx={{ my: '18px' }} />

        <Typography variant='body2' fontWeight='700' mb='2px'>Where this page lives</Typography>
        <Typography variant='caption' color='text.secondary' display='block' mb='10px'>
          Choosing a subdomain also points it here, so the page and its address stay in step.
        </Typography>
        <FormControl size='small' fullWidth sx={{ mb: '6px' }}>
          <InputLabel shrink>Address</InputLabel>
          <Select
            displayEmpty
            label='Address'
            value={form.subdomain || ''}
            onChange={(e) => setForm({ ...form, subdomain: e.target.value })}
          >
            <MenuItem value=''>
              {domain ? `${domain}/${form.slug || ''}` : 'Your main domain'}
            </MenuItem>
            {subdomains.map((sd) => (
              <MenuItem key={sd._id || sd.subdomain} value={sd.subdomain}>
                {domain ? `${sd.subdomain}.${domain}` : sd.subdomain}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* An address dropdown with one option looks broken. Saying why, and
            where to add more, is the difference between "this does nothing"
            and "there is nothing here yet". */}
        <Typography variant='caption' color='text.secondary' display='block' mb='18px'>
          {subdomains.length === 0
            ? (domain
              ? <>No subdomains set up yet — add one under <strong>Domains</strong> to publish pages at shop.{domain} or similar.</>
              : <>Connect a domain under <strong>Domains</strong> first, then pages can live on subdomains of it.</>)
            : 'Pages on a subdomain are reachable only at that address.'}
        </Typography>

        <Typography variant='body2' fontWeight='700' mb='2px'>Detail page</Typography>
        <Typography variant='caption' color='text.secondary' display='block' mb='10px'>
          Makes this one page serve every entry of a content type — one “Blog post” page for
          all your posts, rather than a page each.
        </Typography>
        <FormControl size='small' fullWidth>
          <InputLabel shrink>Shows one entry from</InputLabel>
          <Select
            displayEmpty
            label='Shows one entry from'
            value={form.detailOf || ''}
            onChange={(e) => setForm({ ...form, detailOf: e.target.value })}
          >
            <MenuItem value=''>Not a detail page</MenuItem>
            <MenuItem value='blogs'>Blog posts</MenuItem>
            <MenuItem value='news'>News</MenuItem>
            <MenuItem value='projects'>Projects</MenuItem>
            <MenuItem value='services'>Services</MenuItem>
            <MenuItem value='ourteam'>Our team</MenuItem>
          </Select>
        </FormControl>

      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={() => onSave(form)} disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
